import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useOverlayContext } from '@/app/common/context/OverlayContext';

import { EvoOverlayFilterModal } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/EvoOverlayFilterModal';
import {
  EVO_OVERLAY_DEFAULT_WIDTH,
  EVO_OVERLAY_WIDTHS,
  setEvoOverlayPosition,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/evoOverlayPosition';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import {
  EVO_BODY_INSET,
  EVO_BODY_INSET_MEDIA,
  EVO_BODY_INSET_WIDE,
} from '@/app/components/search/components/Modal/OpenXModal/utils';
import { sumClasses } from '@/app/components/search/utils/misc';
import { useIsDesktop } from '@/app/components/search/utils/useSsrMedia';
import { useTranslation } from '@/app/components/translations/Translation';
import { Brand } from '@/domainLogic/index.ts';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

// #region EvoTransportPill Styles
const common = css`
  box-sizing: border-box;
  min-height: 48px;
  /* MPH-398: Figma footer button text is 16px (size200); size175 rendered 14px */
  font-size: ${({ theme }) => theme.kallisto.font.size['200']};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Clear = styled.div<{ $hasValue?: boolean }>`
  ${common};
  color: ${({ theme }) => theme.kallisto.color.common.black[200]};
  ${({ $hasValue }) =>
    $hasValue &&
    css`
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    `}
`;

const Apply = styled.div`
  ${common};
  gap: ${({ theme }) => theme.kallisto.space.space100};
  padding: ${({ theme }) => `${theme.kallisto.space.space150} ${theme.kallisto.space.space200}`};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
  color: #fff;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryHover};
  }
`;

const CtaContainer = styled.div<{ $showShadow?: boolean }>`
  box-sizing: border-box;
  background-color: #fff;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  /* MPH-398: Figma footer Actions gap is space300 (24px) between Reset and Apply */
  gap: ${({ theme }) => theme.kallisto.space.space300};
  /* MPH-398: padding on all sides so the buttons sit off the sheet edges and clear the scroll shadow.
     MPH-1116: the sides follow the modal body's own inset — 24px while the modal is still a centred
     dialog (this modal renders up to 1023px, so that band is reachable), 16px once it becomes a
     full-width sheet — so the Reset/Apply row lines up with the filters scrolling above it. */
  padding: ${({ theme }) => theme.kallisto.space.space200} ${EVO_BODY_INSET_WIDE};

  @media ${EVO_BODY_INSET_MEDIA} {
    padding: ${({ theme }) => theme.kallisto.space.space200} ${EVO_BODY_INSET};
  }
  /* MPH-398: top shadow when the sheet content scrolls under the footer (matches the header shadow) */
  box-shadow: ${({ $showShadow, theme }) => ($showShadow ? theme.kallisto.shadow.mediumTopShadow : 'none')};
`;

// MPH-398: the footer must read the modal's scroll-shadow context to show the top shadow on overflow.
// The design-system DialogFooter doesn't consume it, and a plain styled footer can't call the hook,
// so this small component (rendered inside the OverlayContext provider) bridges it.
function FilterFooter(props: { hasValue: boolean; onReset: () => void; onApply: () => void; applyLabel: string }) {
  const { hasValue, onReset, onApply, applyLabel } = props;
  const { t } = useTranslation();
  const { footerShadow } = useOverlayContext();
  return (
    <CtaContainer $showShadow={footerShadow}>
      <Clear $hasValue={hasValue} onClick={onReset}>
        {t('vacation.oss.change_transport.filters.reset')}
      </Clear>
      <Apply onClick={onApply}>{applyLabel}</Apply>
    </CtaContainer>
  );
}

/* MPH-398: scrim behind the mobile filter sheet. The OpenXModal Dialog overlay sits below the
 * alternative-flights modal content, so it can't dim it. This backdrop sits above that content
 * (1450) and the filter sheet is raised above the backdrop (1500). */
const MobileFilterBackdrop = styled.div`
  position: fixed;
  inset: 0;
  /* MPH-398: Figma overlay token (rgba(10,12,13,0.5)) */
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundOverlay};
  z-index: 1450;
`;

// #endregion EvoTransportPill Styles

export type EvoFilterModalProps = {
  closeModal: () => void;
  applyFilter: () => void;
  hasActiveFilterSelectedOptions: () => boolean;
  modalTitle: string;
  clearFilter: () => void;
  renderActiveFilterModalContent: () => React.ReactNode;
  isOpen: boolean;
  brand: Brand;
  control: string;
  containerRef?: React.RefObject<HTMLButtonElement | undefined>;
};

export function EvoFilterModal(props: EvoFilterModalProps) {
  const {
    isOpen,
    brand,
    closeModal,
    applyFilter,
    hasActiveFilterSelectedOptions,
    modalTitle,
    clearFilter,
    renderActiveFilterModalContent,
    control,
    containerRef,
  } = props;

  const { commonData, localization } = usePageWrapperData();
  const isDesktop = useIsDesktop(commonData.isDesktopDevice);
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen || !containerRef) return undefined;
    const overlayWidth = EVO_OVERLAY_WIDTHS[control] ?? EVO_OVERLAY_DEFAULT_WIDTH;
    const updateOverlayPosition = () => setEvoOverlayPosition(containerRef, overlayRef, overlayWidth);
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => {
      window.removeEventListener('resize', updateOverlayPosition);
    };
  }, [isOpen, containerRef, control]);

  return (
    <>
      {isOpen &&
        !isDesktop &&
        ReactDOM.createPortal(<MobileFilterBackdrop aria-hidden="true" onClick={closeModal} />, document.body)}
      {isOpen && !isDesktop && (
        <OpenXModal
          isOpen={isOpen}
          onClose={closeModal}
          id={`evo-filter-modal-${control}`}
          className={sumClasses([control || '', 'evo-transport-filter-modal', 'modal-M'])}
          isMobile={!isDesktop}
          brand={brand || Brand.LASTMINUTE}
          scrollShadows={localization?.configValuesApp?.evolutionEnabled}
          style={{ zIndex: 1500 }}
          aria-label={modalTitle}
          header={{
            sticky: true,
            element: <ModalHeader handleClose={closeModal}>{modalTitle}</ModalHeader>,
          }}
          bodyBackgroundColor="gray50"
          footer={{
            sticky: true,
            element: (
              <FilterFooter
                hasValue={hasActiveFilterSelectedOptions()}
                onReset={clearFilter}
                onApply={applyFilter}
                applyLabel={t('vacation.oss.filters.apply')}
              />
            ),
          }}
        >
          {renderActiveFilterModalContent()}
        </OpenXModal>
      )}
      {isOpen &&
        isDesktop &&
        ReactDOM.createPortal(
          <EvoOverlayFilterModal
            overlayRef={overlayRef}
            control={control}
            modalTitle={modalTitle}
            closeModal={closeModal}
            applyFilter={applyFilter}
            hasActiveFilterSelectedOptions={hasActiveFilterSelectedOptions}
            clearFilter={clearFilter}
            renderActiveFilterModalContent={renderActiveFilterModalContent}
          />,
          /* Portal inside the change-flight Radix dialog so its scroll lock, focus trap
             and pointer-events rules apply to the popover (they only cover the dialog subtree) */
          document.getElementById('flight-change-modal') ?? document.body
        )}
    </>
  );
}
